import { Controller } from "@hotwired/stimulus"
import { initCropper, destroyCropper, getCroppedBlob, applyRotation, CROPPER_PRESETS, bindModalActions, unbindModalActions, lockBodyScroll, unlockBodyScroll } from "../helpers/cropper_helper"
import { isHeic, convertHeicToJpeg } from "../helpers/heic_helper"
import { showToast } from "../helpers/toast"
import { showConfirmDialog } from "../helpers/confirm_dialog"

export default class extends Controller {
  static targets = ["input", "preview", "placeholder", "modal", "removeButton"]
  static values = {
    uploadUrl: String,
    paramName: { type: String, default: "recipe[final_photo]" },
    skipReload: { type: Boolean, default: false },
    // "png" preserves transparency (cookbook cover templates); default "jpeg".
    format: { type: String, default: "jpeg" }
  }

  click() {
    if (this.modalEl && !this.modalEl.classList.contains("hidden")) return
    this.inputTarget.click()
  }

  remove() {
    this.inputTarget.value = ""
    if (this.hasPreviewTarget) {
      this.previewTarget.src = ""
      this.previewTarget.classList.add("hidden")
    }
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove("hidden")
    if (this.hasRemoveButtonTarget) this.removeButtonTarget.classList.add("hidden")
  }

  async removePhoto(event) {
    event.preventDefault()
    event.stopPropagation()

    // Read the URL from the button BEFORE awaiting — once the confirm dialog's
    // await yields, event dispatch is over and event.currentTarget is null.
    const url = event.currentTarget.dataset.url

    if (!(await showConfirmDialog("Remove the food photo?"))) return

    const token = document.querySelector("meta[name='csrf-token']")?.content

    fetch(url, {
      method: "DELETE",
      headers: { "X-CSRF-Token": token, Accept: "text/html" }
    }).then((response) => {
      if (!response.ok && !response.redirected) {
        showToast("Couldn't remove that photo. Try again.", { variant: "error" })
        return
      }
      Turbo.visit(window.location.href, { action: "replace" })
    }).catch(() => {
      showToast("Couldn't reach the server. Check your connection.", { variant: "error" })
    })
  }

  removeCover(event) {
    const url = event.currentTarget.dataset.url
    const token = document.querySelector("meta[name='csrf-token']")?.content

    // Update UI immediately — show placeholder
    const coverArea = this.element.querySelector("[data-action*='photo-drop#click']")
    if (coverArea) {
      coverArea.innerHTML = `
        <div class="w-full h-full flex flex-col items-center justify-center gap-1">
          <svg class="h-6 w-6 text-gray-300 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
          <span class="text-xs text-gray-400">Tap to add cover</span>
        </div>`
    }

    // Hide the remove button
    event.currentTarget.classList.add("hidden")

    // Send delete request, then refresh so any tile outside the modal
    // re-renders without its cover image.
    fetch(url, {
      method: "PATCH",
      headers: { "X-CSRF-Token": token, Accept: "text/html" },
      redirect: "manual"
    }).then(() => {
      if (window.Turbo) Turbo.visit(window.location.href, { action: "replace" })
    })
  }

  dragover(e) {
    e.preventDefault()
    this.element.classList.add("ring-warm-400", "bg-warm-50")
  }

  dragleave() {
    this.element.classList.remove("ring-warm-400", "bg-warm-50")
  }

  drop(e) {
    e.preventDefault()
    this.dragleave()
    const file = e.dataTransfer.files[0]
    if (file && (file.type.startsWith("image/") || isHeic(file))) this.prepareAndCrop(file)
  }

  select(event) {
    const file = event.target.files[0]
    if (file) this.prepareAndCrop(file)
  }

  async prepareAndCrop(file) {
    if (isHeic(file)) {
      try {
        const converted = await convertHeicToJpeg(file)
        this.openCrop(converted)
      } catch (e) {
        console.error("[HEIC] Conversion failed:", e)
        showToast("Couldn't process that HEIC photo. Try a JPEG or PNG.", { variant: "error" })
      }
    } else {
      this.openCrop(file)
    }
  }

  openCrop(file) {
    if (!this.hasModalTarget) {
      this.assignFile(file)
      return
    }

    this.modalWrapper = this.modalTarget
    this.modalEl = this.modalWrapper.querySelector("[data-crop-modal]") || this.modalWrapper
    this.cropImage = this.modalEl.querySelector(".js-crop-image")

    const reader = new FileReader()
    reader.onload = (e) => {
      document.body.appendChild(this.modalWrapper)
      this.modalEl.classList.remove("hidden")
      // Prevent clicks inside crop modal from closing parent dropdown
      this.modalWrapper.addEventListener("click", (ev) => ev.stopPropagation())
      lockBodyScroll()
      this.handlers = bindModalActions(this.modalEl, {
        close: () => this.closeCrop(),
        apply: () => this.applyCrop(),
        rotateLeft: () => this.rotateLeft(),
        rotateRight: () => this.rotateRight(),
        zoomIn: () => this.zoomIn(),
        zoomOut: () => this.zoomOut()
      })

      this.cropImage.onload = () => {
        this.cropper = destroyCropper(this.cropper)
        this.cropper = initCropper(this.cropImage, CROPPER_PRESETS.photo)
      }
      this.cropImage.src = e.target.result
    }
    reader.readAsDataURL(file)
  }

  closeCrop() {
    this.cropper = destroyCropper(this.cropper)
    this.handlers = unbindModalActions(this.modalEl, this.handlers)
    this.modalEl.classList.add("hidden")
    this.element.appendChild(this.modalWrapper)
    unlockBodyScroll()
    this.inputTarget.value = ""
  }

  async applyCrop() {
    if (!this.cropper) return

    const blob = await getCroppedBlob(this.cropper, { format: this.formatValue })
    if (!blob) return

    this.cropper = destroyCropper(this.cropper)
    this.handlers = unbindModalActions(this.modalEl, this.handlers)
    this.modalEl.classList.add("hidden")
    this.element.appendChild(this.modalWrapper)

    // Only unlock body scroll if no parent modal/dropdown is open
    const parentModal = this.element.closest("[data-dropdown-target='menu']:not(.hidden), [data-modal-target='dialog']:not(.hidden)")
    if (!parentModal) unlockBodyScroll()

    const png = this.formatValue === "png"
    this.assignFile(new File([blob], png ? "cover.png" : "photo.jpg", { type: png ? "image/png" : "image/jpeg" }))
  }

  zoomIn() { if (this.cropper) this.cropper.zoom(0.1) }
  zoomOut() { if (this.cropper) this.cropper.zoom(-0.1) }

  async rotateLeft() {
    if (!this.cropper) return
    this.cropper = await applyRotation(this.cropper, this.cropImage, -90, "photo")
  }

  async rotateRight() {
    if (!this.cropper) return
    this.cropper = await applyRotation(this.cropper, this.cropImage, 90, "photo")
  }

  assignFile(file) {
    const dt = new DataTransfer()
    dt.items.add(file)
    this.inputTarget.files = dt.files

    // Direct upload if uploadUrl is set
    if (this.hasUploadUrlValue && this.uploadUrlValue) {
      this.uploadDirect(file)
      return
    }

    if (this.hasPreviewTarget) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.previewTarget.src = e.target.result
        this.previewTarget.classList.remove("hidden")
        if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add("hidden")
        if (this.hasRemoveButtonTarget) this.removeButtonTarget.classList.remove("hidden")
      }
      reader.readAsDataURL(file)
    }
  }

  uploadDirect(file) {
    const formData = new FormData()
    formData.append(this.paramNameValue, file)
    const token = document.querySelector("meta[name='csrf-token']")?.content

    this.showInstantPreview(file)

    // Let fetch follow the 302 from Rails' redirect_to so response.ok cleanly
    // reflects success/failure. `redirect: "manual"` returned different types
    // across browsers and produced false-negative error alerts.
    const accept = this.skipReloadValue ? "application/json" : "text/html"
    fetch(this.uploadUrlValue, {
      method: "PATCH",
      headers: { "X-CSRF-Token": token, Accept: accept },
      body: formData
    }).then((response) => {
      // `response.redirected` is true when fetch followed a 302 from
      // `redirect_back` — that's success, not failure. Without this
      // check, HEIC uploads through the SW path were tripping a
      // false "Couldn't save" toast even though the photo had attached.
      if (!response.ok && !response.redirected) {
        showToast("Couldn't save that photo. Try a JPEG or PNG.", { variant: "error" })
        return
      }
      if (this.skipReloadValue) return
      if (window.Turbo) Turbo.visit(window.location.href, { action: "replace" })
    }).catch(() => {
      showToast("Couldn't reach the server. Check your connection.", { variant: "error" })
    })
  }

  // Use the dedicated preview target when available (placeholder/empty case);
  // otherwise hijack the click area so the user sees instant feedback while
  // the PATCH is in flight.
  showInstantPreview(file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      if (this.hasPreviewTarget) {
        this.previewTarget.src = e.target.result
        this.previewTarget.classList.remove("hidden")
        if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add("hidden")
      } else {
        const coverArea = this.element.querySelector("[data-action*='photo-drop#click']")
        if (coverArea) coverArea.innerHTML = `<img src="${e.target.result}" class="w-full h-full object-cover" />`
      }
      // Let an enclosing cover editor repaint its live preview with the new image.
      this.dispatch("preview")
    }
    reader.readAsDataURL(file)
  }

  disconnect() {
    this.cropper = destroyCropper(this.cropper)
    if (this.modalWrapper?.parentElement === document.body) {
      this.handlers = unbindModalActions(this.modalEl, this.handlers)
      this.element.appendChild(this.modalWrapper)
    }
    unlockBodyScroll()
  }
}
